import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Check,
  ExternalLink,
  MousePointerClick,
  Globe2,
  Calendar,
  Smartphone,
  Monitor,
  Tablet,
  Chrome,
  Globe,
  BarChart3,
  Lock,
  UserCheck,
  Mail,
  Phone,
  User,
  AlertTriangle,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import {
  Link as LinkType,
  ClickLog,
  GhlContact,
  AnalyticsSummary,
} from "../../../types";
import RichStatCard from "../../../components/ui/RichStatCard";
import EmptyState from "../../../components/ui/EmptyState";
import Button from "../../../components/ui/Button";
import Badge from "../../../components/ui/Badge";
import Skeleton, {
  SkeletonStat,
} from "../../../components/ui/Skeleton";
import Pagination from "../../../components/ui/Pagination";
import WorldMap from "../../../components/ui/WorldMap";
import HelpHint from "../../../components/ui/HelpHint";
import {
  buildRedirectUrl,
  copyToClipboard,
  formatDate,
  formatDateTime,
  formatNumber,
} from "../../../utils";

const CLICKS_PER_PAGE = 10;

type Range = "7d" | "30d" | "90d" | "all";
// Data window sent to the server per range. "all" uses a large span; the server
// further bounds it by the account's retention window.
const RANGE_TO_DAYS: Record<Range, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: 36500,
};
// How many trailing days the time-series chart plots for each range.
const CHART_DAYS: Record<Range, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: 90,
};

const dayKey = (d: Date) => d.toISOString().slice(0, 10);

// Zero-fill the server's per-day counts into a continuous trailing window.
const fillDailyWindow = (
  daily: { date: string; clicks: number }[],
  days: number,
) => {
  const counts = new Map(daily.map((d) => [d.date, d.clicks]));
  const out: { date: string; clicks: number }[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = dayKey(d);
    out.push({ date: key.slice(5), clicks: counts.get(key) || 0 });
  }
  return out;
};

const domainOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const contactDisplayName = (c?: GhlContact) => {
  if (!c) return "";
  return (
    c.contactName ||
    c.name ||
    [c.firstName, c.lastName].filter(Boolean).join(" ") ||
    c.email ||
    c.phone ||
    ""
  );
};

const HeroFavicon = ({ url }: { url: string }) => {
  const [error, setError] = useState(false);
  const domain = domainOf(url);
  if (error || !domain) {
    return (
      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-100 to-violet-100 text-brand-600 flex items-center justify-center font-display font-bold text-lg shrink-0">
        {domain.charAt(0).toUpperCase() || "·"}
      </div>
    );
  }
  return (
    <div className="w-12 h-12 rounded-2xl bg-white border border-ink-200/70 shadow-sm flex items-center justify-center shrink-0 overflow-hidden">
      <img
        src={`https://www.google.com/s2/favicons?sz=64&domain=${domain}`}
        alt=""
        className="w-7 h-7"
        onError={() => setError(true)}
      />
    </div>
  );
};

const BreakdownList = ({
  items,
  emptyLabel,
  total,
  accent = "brand",
  capitalize = true,
  mono = false,
}: {
  items: { name: string; value: number }[];
  emptyLabel: string;
  total: number;
  accent?: "brand" | "emerald" | "violet" | "amber";
  capitalize?: boolean;
  mono?: boolean;
}) => {
  const gradients: Record<string, string> = {
    brand: "from-brand-500 to-violet-500",
    emerald: "from-emerald-500 to-teal-500",
    violet: "from-violet-500 to-fuchsia-500",
    amber: "from-amber-500 to-orange-500",
  };
  if (items.length === 0) {
    return (
      <p className="text-sm text-ink-400 text-center py-6">{emptyLabel}</p>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const pct = total > 0 ? (item.value / total) * 100 : 0;
        return (
          <div key={item.name}>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span
                className={`text-ink-800 truncate pr-2 font-medium ${capitalize ? "capitalize" : ""} ${mono ? "font-mono text-xs" : ""}`}
                title={item.name}
              >
                {item.name}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-ink-400 text-xs tabular-nums">
                  {pct.toFixed(1)}%
                </span>
                <span className="text-ink-900 font-semibold text-xs tabular-nums w-7 text-right">
                  {item.value}
                </span>
              </div>
            </div>
            <div className="w-full h-1.5 bg-ink-100 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${gradients[accent]} rounded-full transition-all duration-700`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const deviceIcon = (type?: string) => {
  if (type === "mobile") return <Smartphone size={13} />;
  if (type === "tablet") return <Tablet size={13} />;
  return <Monitor size={13} />;
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.[0]) return null;
  return (
    <div className="bg-ink-900 text-white rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-ink-400 mb-0.5 text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div className="font-semibold font-display text-base">
        {payload[0].value}{" "}
        <span className="text-xs font-normal text-ink-400">
          {payload[0].value === 1 ? "click" : "clicks"}
        </span>
      </div>
    </div>
  );
};

const RangeChips = ({
  range,
  setRange,
  retentionDays,
  onLockedClick,
}: {
  range: Range;
  setRange: (r: Range) => void;
  retentionDays: number;
  onLockedClick: () => void;
}) => {
  const items: { value: Range; label: string; days: number }[] = [
    { value: "7d", label: "7d", days: 7 },
    { value: "30d", label: "30d", days: 30 },
    { value: "90d", label: "90d", days: 90 },
    { value: "all", label: "All", days: Infinity },
  ];
  return (
    <div className="inline-flex p-0.5 bg-ink-100 rounded-lg">
      {items.map((it) => {
        const locked = it.days > retentionDays;
        const active = range === it.value && !locked;
        return (
          <button
            key={it.value}
            onClick={() => (locked ? onLockedClick() : setRange(it.value))}
            title={
              locked
                ? `Available with longer data retention — upgrade for ${it.label} of history`
                : undefined
            }
            className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-medium transition-all ${
              active
                ? "bg-white text-ink-900 shadow-sm"
                : locked
                ? "text-ink-300"
                : "text-ink-500 hover:text-ink-900"
            }`}
          >
            {locked && <Lock size={10} className="text-ink-400" />}
            {it.label}
          </button>
        );
      })}
    </div>
  );
};

const CardShell = ({
  title,
  subtitle,
  action,
  help,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  help?: { anchor?: string; body: React.ReactNode };
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden ${className}`}
  >
    <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
      <div>
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-ink-900 tracking-tight">
          {title}
          {help && (
            <HelpHint title={title} anchor={help.anchor}>
              {help.body}
            </HelpHint>
          )}
        </h3>
        {subtitle && (
          <p className="text-xs text-ink-500 mt-0.5">{subtitle}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    <div className="px-5 pb-5">{children}</div>
  </div>
);

const MiniStat = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-xl border border-ink-200/70 bg-ink-50/40 px-3 py-2.5">
    <div className="text-lg font-display font-bold text-ink-900 tabular-nums">
      {formatNumber(value)}
    </div>
    <div className="text-[11px] text-ink-500">{label}</div>
  </div>
);

const AnalyticsDetail = () => {
  const { id } = useParams<{ id: string }>();
  const services = useAppServices();
  const { buildPath, features, openUpgrade } = useMarketplace();

  const [link, setLink] = useState<LinkType | null>(null);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [pageData, setPageData] = useState<{ items: ClickLog[]; total: number }>(
    { items: [], total: 0 },
  );
  // key absent = not fetched yet (loading); null = fetched but not found in GHL;
  // object = found.
  const [contacts, setContacts] = useState<Record<string, GhlContact | null>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [clicksPage, setClicksPage] = useState(1);
  // Default to the largest range still inside the plan's retention window.
  const [range, setRange] = useState<Range>(() =>
    features.dataRetentionDays >= 30 ? "30d" : "7d",
  );

  // Link + server-rolled-up summary. Refetched whenever the range changes (the
  // window is now a server query param, not a client-side filter).
  const fetchSummary = useCallback(
    async (showLoading = false) => {
      if (!id) return;
      if (showLoading) setLoading(true);
      const days = RANGE_TO_DAYS[range];
      const [linkRes, summaryRes] = await Promise.all([
        services.url_tracker.filter({ query: `_id=${id}` }),
        services.url_tracker_logs.summaryByTracker({
          query: `url_tracker_id=${id}&days=${days}`,
        }),
      ]);
      if (linkRes.response) setLink(linkRes.response);
      if (summaryRes.response)
        setSummary(summaryRes.response as AnalyticsSummary);
      if (showLoading) setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, range],
  );

  // One page of raw click rows for the "Recent clicks" table.
  const fetchPage = useCallback(
    async () => {
      if (!id) return;
      const days = RANGE_TO_DAYS[range];
      const { response } = await services.url_tracker_logs.page({
        query: `url_tracker_id=${id}&days=${days}&page=${clicksPage}&limit=${CLICKS_PER_PAGE}`,
      });
      if (response)
        setPageData(response as { items: ClickLog[]; total: number });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, range, clicksPage],
  );

  useEffect(() => {
    fetchSummary(true);
  }, [fetchSummary]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Reset to the first page whenever the range changes.
  useEffect(() => {
    setClicksPage(1);
  }, [range]);

  // Resolve GHL contact details for clicks that carried a contact_id (drawn from
  // the summary's top contacts + the current page's rows). Cached by id so each
  // contact is fetched once. Best-effort — failures are ignored.
  useEffect(() => {
    const ids = Array.from(
      new Set([
        ...(summary?.contacts ?? []).map((c) => c.contact_id),
        ...(pageData.items
          .map((l) => l.contact_id)
          .filter(Boolean) as string[]),
      ]),
    ).slice(0, 100);
    const missing = ids.filter((cid) => !(cid in contacts));
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        missing.map(async (cid) => {
          const { response } = await services.ghl.contact({
            query: `id=${encodeURIComponent(cid)}`,
          });
          return [cid, (response as GhlContact) || null] as const;
        }),
      );
      if (cancelled) return;
      setContacts((prev) => {
        const next = { ...prev };
        // Record null for ids not found in GHL so we stop "loading" them.
        for (const [cid, c] of results) next[cid] = c;
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, pageData]);

  const shortUrl = id
    ? buildRedirectUrl(link?.slug || id, link?.domain_host, link?.append_contact_id)
    : "";
  const handleCopy = async () => {
    if (!shortUrl) return;
    const ok = await copyToClipboard(shortUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  // Total clicks in the selected window (non-bot) — straight from the server.
  const total = summary?.total ?? 0;
  const pagedClicks = pageData.items;

  const timeSeries = useMemo(
    () => fillDailyWindow(summary?.daily ?? [], CHART_DAYS[range]),
    [summary, range],
  );

  const sparkData = timeSeries.map((d) => d.clicks);

  // Breakdowns arrive pre-aggregated + sorted; slice to each panel's count.
  const countries = useMemo(() => (summary?.countries ?? []).slice(0, 6), [summary]);
  const cities = useMemo(() => (summary?.cities ?? []).slice(0, 6), [summary]);
  const devices = useMemo(() => (summary?.devices ?? []).slice(0, 5), [summary]);
  const browsers = useMemo(() => (summary?.browsers ?? []).slice(0, 6), [summary]);
  const referrers = useMemo(() => (summary?.sources ?? []).slice(0, 6), [summary]);
  const oses = useMemo(() => (summary?.os ?? []).slice(0, 6), [summary]);
  const destinations = useMemo(
    () =>
      (summary?.destinations ?? []).slice(0, 8).map((d) => ({
        name: d.name.replace(/^https?:\/\//, ""),
        value: d.value,
      })),
    [summary],
  );
  const uniqueDestinations = summary?.destinations?.length ?? 0;
  // Already {name, code, value}, sorted by the server — feeds the world map.
  const countryEntries = summary?.countries ?? [];
  const uniqueCountries = summary?.countries.length ?? 0;
  const uniqueCities = summary?.uniqueCities ?? 0;

  // CRM contact engagement — counts come from the server (clicks that carried a
  // contact_id); the client only resolves contact_ids to display names.
  const contactClicks = useMemo(
    () => (summary?.contacts ?? []).reduce((s, c) => s + c.value, 0),
    [summary],
  );
  const uniqueContacts = summary?.contacts?.length ?? 0;
  const topContacts = useMemo(
    () =>
      (summary?.contacts ?? []).slice(0, 8).map((c) => ({
        name:
          contactDisplayName(contacts[c.contact_id] || undefined) ||
          c.contact_id,
        value: c.value,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary, contacts],
  );

  const anyContactClicks = (summary?.contacts?.length ?? 0) > 0;
  const hasContactFeatures =
    (link?.click_tags?.length || 0) > 0 || anyContactClicks;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-44 w-full" rounded="rounded-3xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
        </div>
        <Skeleton className="h-80 w-full" rounded="rounded-2xl" />
      </div>
    );
  }

  if (!link) {
    return (
      <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
        <EmptyState
          icon={<BarChart3 size={26} />}
          tone="amber"
          title="Link not found"
          description="This link may have been deleted, or the URL is incorrect."
          action={
            <RouterLink to={buildPath("analytics")}>
              <Button variant="secondary">Back to analytics</Button>
            </RouterLink>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* HERO */}
      <section className="relative overflow-hidden rounded-3xl border border-ink-200/60 bg-white shadow-card">
        <div className="absolute inset-0 bg-mesh-1 opacity-100" />
        <div className="absolute inset-0 bg-dotted bg-dot-md opacity-30" />
        <div className="absolute -top-32 -right-24 w-96 h-96 bg-brand-gradient opacity-[0.1] rounded-full blur-3xl" />

        <div className="relative px-7 py-7 sm:px-10 sm:py-9">
          <RouterLink
            to={buildPath("analytics")}
            className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900 mb-4"
          >
            <ArrowLeft size={12} /> Back to analytics
          </RouterLink>

          <div className="flex items-start gap-4">
            <HeroFavicon url={link.url} />
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-h1 text-ink-900 truncate">
                {link.title || "Untitled"}
              </h1>
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-brand-600 mt-1 max-w-full"
              >
                <span className="truncate">{link.url}</span>
                <ExternalLink size={11} className="shrink-0" />
              </a>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-white/80 backdrop-blur border border-ink-200/70 px-2.5 h-9 inline-flex items-center rounded-lg font-mono text-brand-700">
              {shortUrl}
            </code>
            <Button
              variant="secondary"
              size="md"
              icon={
                copied ? (
                  <Check size={13} className="text-emerald-600" />
                ) : (
                  <Copy size={13} />
                )
              }
              onClick={handleCopy}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
            <Badge tone="emerald" dot className="ml-auto">
              Tracking active
            </Badge>
          </div>
        </div>
      </section>

      {/* Stats with sparklines */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <RichStatCard
          label="Total clicks"
          value={total}
          data={sparkData}
          tone="brand"
          icon={<MousePointerClick size={16} strokeWidth={2.25} />}
        />
        <RichStatCard
          label="Countries"
          value={uniqueCountries}
          data={sparkData}
          tone="emerald"
          icon={<Globe2 size={16} strokeWidth={2.25} />}
        />
        <RichStatCard
          label="Unique cities"
          value={uniqueCities}
          data={sparkData}
          tone="violet"
          icon={<Globe size={16} strokeWidth={2.25} />}
        />
        <RichStatCard
          label="Created"
          value={0}
          data={[]}
          tone="amber"
          icon={<Calendar size={16} strokeWidth={2.25} />}
          format={() => formatDate(link.createdAt)}
        />
      </section>

      {/* Big chart */}
      <CardShell
        title="Clicks over time"
        help={{
          anchor: "analytics-time",
          body: "Clicks per day across the selected window. Use the range chips (7d / 30d / 90d / All) to zoom in or out. Buckets are in UTC.",
        }}
        subtitle="Trends across the selected window."
        action={
          <RangeChips
            range={range}
            setRange={setRange}
            retentionDays={features.dataRetentionDays}
            onLockedClick={openUpgrade}
          />
        }
      >
        {total === 0 ? (
          <p className="text-sm text-ink-400 text-center py-10">
            No clicks in this window. Try expanding the range, or share your
            link to start collecting data.
          </p>
        ) : (
          <div className="h-72 -mx-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={timeSeries}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="dClickGrad"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f4f4f5" vertical={false} />
                <XAxis
                  dataKey="date"
                  fontSize={11}
                  stroke="#a1a1aa"
                  tickLine={false}
                  axisLine={false}
                  dy={4}
                />
                <YAxis
                  fontSize={11}
                  stroke="#a1a1aa"
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  width={28}
                />
                <Tooltip
                  content={<ChartTooltip />}
                  cursor={{ stroke: "#a1a1aa", strokeDasharray: "3 3" }}
                />
                <Area
                  type="monotone"
                  dataKey="clicks"
                  stroke="#6366f1"
                  strokeWidth={2.25}
                  fill="url(#dClickGrad)"
                  animationDuration={600}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardShell>

      {/* Geographic distribution */}
      <CardShell
        title="Geographic distribution"
        help={{
          anchor: "analytics-map",
          body: "Each country is shaded by click volume, with the top countries and cities listed below. Location is detected from offline GeoIP data — no third-party tracking.",
        }}
        subtitle={
          uniqueCountries > 0
            ? `${uniqueCountries} ${uniqueCountries === 1 ? "country" : "countries"} reached`
            : "No geo data yet"
        }
      >
        <WorldMap entries={countryEntries} />
      </CardShell>

      {/* Bento breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CardShell title="Top countries" subtitle="Where clicks come from">
          <BreakdownList
            items={countries}
            emptyLabel="No data yet"
            total={total}
            accent="brand"
          />
        </CardShell>
        <CardShell title="Top cities" subtitle="From GeoIP databases">
          <BreakdownList
            items={cities}
            emptyLabel="No city data yet"
            total={total}
            accent="violet"
          />
        </CardShell>
        <CardShell title="Devices" subtitle="Mobile, desktop, tablet">
          <BreakdownList
            items={devices}
            emptyLabel="No data yet"
            total={total}
            accent="emerald"
          />
        </CardShell>
        <CardShell title="Operating systems" subtitle="iOS, Android, Windows…">
          <BreakdownList
            items={oses}
            emptyLabel="No data yet"
            total={total}
            accent="violet"
            capitalize={false}
          />
        </CardShell>
        <CardShell title="Browsers" subtitle="Top browsers in use">
          <BreakdownList
            items={browsers}
            emptyLabel="No data yet"
            total={total}
            accent="brand"
          />
        </CardShell>
        <CardShell
          title="Referrers"
          subtitle="Where users came from"
          className="lg:col-span-2"
        >
          <BreakdownList
            items={referrers}
            emptyLabel="No referrer data yet"
            total={total}
            accent="amber"
          />
        </CardShell>
        <CardShell
          title="Destinations"
          help={{
            anchor: "analytics-destinations",
            body: "The actual URLs this link sent people to. When you run geo, device or A/B rules, this shows how traffic was split across the variants.",
          }}
          subtitle={
            uniqueDestinations > 1
              ? `${uniqueDestinations} URLs served — compare A/B, geo & device variants`
              : "Where clicks were sent"
          }
          className="lg:col-span-3"
        >
          <BreakdownList
            items={destinations}
            emptyLabel="No data yet"
            total={total}
            accent="emerald"
            capitalize={false}
            mono
          />
        </CardShell>
      </div>

      {/* CRM contact engagement */}
      {hasContactFeatures && (
        <CardShell
          title="CRM contact engagement"
          help={{
            anchor: "analytics-contacts",
            body: "For links sent to CRM contacts: how many clicks came from identified contacts, how many unique contacts engaged, and your top contacts by clicks (names resolved from your CRM).",
          }}
          subtitle="Per-contact clicks and tag-on-click automation"
          action={
            <Badge tone="brand" dot>
              CRM
            </Badge>
          }
        >
          {(link.click_tags?.length || 0) > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-1.5 text-xs text-ink-500 mb-2">
                <UserCheck size={13} className="text-brand-500" />
                Tags applied to the contact on every click
              </div>
              <div className="flex flex-wrap gap-1.5">
                {link.click_tags!.map((t) => (
                  <Badge key={t} tone="brand">
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <MiniStat label="Contact clicks" value={contactClicks} />
            <MiniStat label="Unique contacts" value={uniqueContacts} />
            <MiniStat
              label="Anonymous clicks"
              value={Math.max(0, total - contactClicks)}
            />
          </div>
          <div className="text-xs text-ink-500 mb-2">Top contacts by clicks</div>
          <BreakdownList
            items={topContacts}
            emptyLabel="No identified contacts yet — send links with ?contact_id={{contact.id}} from your CRM."
            total={contactClicks}
            accent="violet"
            capitalize={false}
          />
        </CardShell>
      )}

      {/* Recent clicks */}
      <CardShell
        title="Recent clicks"
        subtitle="Latest events as they happen"
        action={
          <Badge tone="emerald" dot>
            Live
          </Badge>
        }
      >
        {total === 0 ? (
          <p className="text-sm text-ink-400 text-center py-6">
            No clicks yet.
          </p>
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 border-b border-ink-100">
                <tr>
                  <th className="text-left px-5 py-3">When</th>
                  <th className="text-left px-5 py-3">Contact</th>
                  <th className="text-left px-5 py-3">Location</th>
                  <th className="text-left px-5 py-3">Device</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">
                    Browser
                  </th>
                  <th className="text-left px-5 py-3 hidden lg:table-cell">
                    Referrer
                  </th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">
                    IP
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100/80">
                {pagedClicks.map((log) => (
                  <tr key={log._id} className="hover:bg-ink-50/60">
                    <td className="px-5 py-2.5 text-ink-700 whitespace-nowrap text-xs">
                      {formatDateTime(log.createdAt)}
                    </td>
                    <td className="px-5 py-2.5 text-ink-700 text-xs">
                      {log.contact_id ? (
                        (() => {
                          const cid = log.contact_id;
                          const fetched = cid in contacts;
                          const c = contacts[cid];
                          if (!fetched) {
                            return (
                              <span className="text-ink-400">Loading…</span>
                            );
                          }
                          if (!c) {
                            return (
                              <span className="inline-flex items-center gap-1 text-[11px] text-amber-700">
                                <AlertTriangle size={11} className="shrink-0" />
                                Contact not found
                              </span>
                            );
                          }
                          const name = contactDisplayName(c);
                          return (
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 font-medium text-ink-800">
                                <User size={11} className="text-brand-500 shrink-0" />
                                <span className="truncate max-w-[160px]">
                                  {name || cid}
                                </span>
                              </div>
                              {c.email && (
                                <div className="flex items-center gap-1 text-[11px] text-ink-500 mt-0.5">
                                  <Mail size={10} className="shrink-0" />
                                  <span className="truncate max-w-[160px]">
                                    {c.email}
                                  </span>
                                </div>
                              )}
                              {c.phone && (
                                <div className="flex items-center gap-1 text-[11px] text-ink-500 mt-0.5">
                                  <Phone size={10} className="shrink-0" />
                                  <span className="truncate max-w-[160px]">
                                    {c.phone}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-ink-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-ink-700">
                      <div className="flex items-center gap-1.5">
                        {log.country_code && (
                          <span className="text-[10px] font-mono uppercase text-ink-500 bg-ink-100 px-1.5 py-0.5 rounded">
                            {log.country_code}
                          </span>
                        )}
                        <span className="text-xs">
                          {[log.city, log.country].filter(Boolean).join(", ") ||
                            "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-ink-700 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="text-ink-400">
                          {deviceIcon(log.device_type)}
                        </span>
                        <span className="capitalize">
                          {log.device_type || "?"}
                        </span>
                        <span className="text-ink-400">· {log.os || "?"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-ink-700 hidden md:table-cell text-xs">
                      <div className="flex items-center gap-1.5">
                        {/Chrome/i.test(log.browser || "") ? (
                          <Chrome size={12} className="text-ink-400" />
                        ) : (
                          <Globe size={12} className="text-ink-400" />
                        )}
                        {log.browser || "?"}
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-ink-700 hidden lg:table-cell text-xs">
                      {log.referrer_platform || "Direct"}
                    </td>
                    <td className="px-5 py-2.5 text-ink-500 font-mono text-xs hidden md:table-cell">
                      {log.ip || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={clicksPage}
              pageSize={CLICKS_PER_PAGE}
              total={pageData.total}
              onPage={setClicksPage}
              className="px-5 pt-4"
            />
          </div>
        )}
      </CardShell>
    </div>
  );
};

export default AnalyticsDetail;
